<template>
  <div class="center-flex">
    <h2>{{ siteName }} File List for Site</h2>
    <Divider />

  </div>
  <div class="center-flex">
    <div class="vertical-stack">
      <label for="file-filter" @click="toggleFilter">Filter:</label>
      <MultiSelect
        v-if="showFilter"
        v-model="selectedFilters"
        :options="filterOptions"
        placeholder="Select filters"
        class="stretch-horizontal"
        :maxSelectedLabels="3"
        selectedItemsLabel="{0} items selected"
      />
      <div>
        <button @click="clearFilters">Clear Filters</button>
      </div>
      <div class="flex flex-row">
        <!-- <label for="start-date">Start Date:</label> -->
        <!-- <input type="date" id="start-date" v-model="startDate" /> -->
         <CustomDatepicker v-model:date="startDate" header="Start Date" />
        <CustomDatepicker v-model:date="endDate" header="End Date" />
      </div>
      <Divider />
      <div class="flex justify-end mb-2">
        <Button 
          icon="pi pi-download" 
          label="Download Selected" 
          @click="downloadSelectedFiles"
          :disabled="!hasSelectedFiles"
          class="p-button-sm"
        />
      </div>
      <div v-if="filteredFiles.length === 0">
        <thead>
          <tr>
            <th>No files found for site.</th>
          </tr>
        </thead>
      </div>
      <div v-else>
        <table class="stretch-horizontal">
          <thead>
            <tr>
              <th>Name</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="file in filteredFiles" :key="file.name" @click="onFileClick(file)">
              <td>
                <div class="flex items-center">
                  <Checkbox v-model="selectedFiles" :value="file" class="mr-2" />
                  <div>{{ file.name }}</div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue'
import type { File } from '@/composables/useFiles'
import type { PropType } from 'vue'
import CustomDatepicker from '@/components/CustomDatepicker.vue';
import { MultiSelect, Button, Checkbox } from 'primevue';

export default {
  name: 'FileList',
  props: {
    siteName: {
      type: String,
      required: true
    },
    files: {
      type: Array as PropType<File[]>,
      required: true
    }
  },
  components: {
    CustomDatepicker,
    MultiSelect,
    Button,
    Checkbox
  },
  setup(props, { emit }) {
    const showFilter = ref(true)
    const selectedFilters = ref<string[]>([])
    const startDate = ref(new Date())
    const endDate = ref(new Date())
    const selectedFiles = ref<File[]>([])

    // set start date to 2000 and end date to 2030
    startDate.value = new Date('2000')
    endDate.value = new Date('2030')
    
    const hasSelectedFiles = computed(() => selectedFiles.value.length > 0)

    const downloadSelectedFiles = async () => {
      for (const file of selectedFiles.value) {
        try {
          const response = await fetch(`/api/media/protected_image/${props.siteName}/${file.name}`)
          if (!response.ok) throw new Error(`Failed to download ${file.name}`)
          
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = file.name
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(a)
        } catch (error) {
          console.error(`Error downloading ${file.name}:`, error)
        }
      }
    }

    // Extract first two underscore-separated words
    const filterOptions = computed(() => {
      const options = props.files.map(file => {
        const parts = file.name.split('_');
        return parts.slice(0, 2).join(' ');
      });
      return [...new Set(options)]; // Ensure unique options
    });

    const clearFilters = () => {
      selectedFilters.value = [];
      startDate.value = new Date('2000');
      endDate.value = new Date('2030');
    }

    // Filter files based on selected filter and date range
    const filteredFiles = computed(() => {
      let filtered = props.files;
      
      // Apply name filter if selected
      if (selectedFilters.value.length > 0) {
        filtered = filtered.filter(file => {
          const parts = file.name.split('_');
          const fileFilter = parts.slice(0, 2).join(' ');
          return selectedFilters.value.includes(fileFilter);
        });
      }

      // Apply date range filter
      if (startDate.value && endDate.value) {
        filtered = filtered.filter(file => {
          // Extract date from filename (format: VH_stack_1_visit_1_20210101t061430.tif or _20170125.tiff)
          const dateMatch = file.name.match(/(\d{8})(?:t|\.tiff?)/);
          // If no date in filename, include the file anyway
          if (!dateMatch) return true;
          
          const fileDate = new Date(
            dateMatch[1].replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')
          );
          return fileDate >= startDate.value && fileDate <= endDate.value;
        });
      }

      return filtered;
    });

    // Emit when a file is clicked
    const onFileClick = (file: File) => {
      emit('file-click', file)
    }

    const toggleFilter = () => {
      showFilter.value = !showFilter.value
    }

    return { toggleFilter, showFilter, selectedFilters, clearFilters, startDate, endDate, filterOptions, filteredFiles, onFileClick, selectedFiles, hasSelectedFiles, downloadSelectedFiles }
  }
}
</script>

<style scoped>
.centre-flex {
  display: flex;
  justify-content: center;
  align-items: center;
}
.vertical-stack {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.stretch-horizontal {
  flex-grow: 1;
  width: 95%;
}
.flex {
  display: flex;
}
.items-center {
  align-items: center;
}
.justify-end {
  justify-content: flex-end;
}
.mb-2 {
  margin-bottom: 0.5rem;
}
.mr-2 {
  margin-right: 0.5rem;
}
</style>
